import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  api, money, unitPrice, quantity, date,
  DOC_TYPE_LABEL, STATUS_LABEL, STATUS_CLASS,
} from '../api.js';
import { usePaymentMethods } from '../hooks.js';

/**
 * Прегледът на документ, без да се напуска справката.
 *
 * От „Печалба" (и другаде, откъдето се преглежда документ извън списъка с
 * документи) редът, филтрите и скролът трябва да си останат, каквито са
 * били — затова е попъп, не навигация към /documents/:id. Само за
 * четене: действията (запис, сторно, редакция) са в самата страница на
 * документа, тук е само справка.
 */
export default function DocumentPreviewModal({ documentId, onClose }) {
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);
  const { byCode } = usePaymentMethods();

  useEffect(() => {
    setDoc(null);
    setError(null);
    api.get(`/api/documents/${documentId}`).then(setDoc).catch((e) => setError(e.message));
  }, [documentId]);

  const lineSum = (it) => it.line_subtotal;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 820 }} onClick={(e) => e.stopPropagation()}>
        <div className="card-head">
          {doc ? `${DOC_TYPE_LABEL[doc.doc_type]} № ${doc.doc_number}` : 'Документ'}
          {doc && (
            <span className={`badge ${STATUS_CLASS[doc.status]}`} style={{ marginLeft: 8 }}>
              {STATUS_LABEL[doc.status]}
            </span>
          )}
          <div className="spacer" />
          {doc && (
            <Link to={`/documents/${doc.id}`} className="button" onClick={onClose}>
              пълен изглед
            </Link>
          )}
          <button className="link" style={{ marginLeft: 12, fontSize: 20 }} onClick={onClose} title="затвори">×</button>
        </div>

        {error && <div className="error" style={{ margin: 14 }}>{error}</div>}
        {!doc && !error && <div className="empty">Зареждане…</div>}

        {doc && (
          <>
            <div className="card-body">
              <div className="doc-grid">
                <F k="Дата" v={date(doc.doc_date)} />
                <F k="Контрагент" v={doc.partner_name ?? 'на каса'} />
                <F k="Склад" v={doc.warehouse_name ?? '—'} />
                <F k="Плащане" v={byCode[doc.payment_method]?.name ?? doc.payment_method} />
                {doc.invoice_no && <F k="Фактура" v={`№ ${doc.invoice_no} / ${date(doc.invoice_date)}`} />}
                {doc.unp && <F k="УНП" v={doc.unp} />}
              </div>
            </div>

            <div className="table-wrap" style={{ maxHeight: '45vh' }}>
              <table className="compact">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>№</th>
                    <th>Наименование</th>
                    <th>Партида</th>
                    <th className="num">Количество</th>
                    <th className="num">Ед. цена без ДДС</th>
                    <th className="num">Сума без ДДС</th>
                  </tr>
                </thead>
                <tbody>
                  {doc.items.map((it) => {
                    const anuliran = Number(it.is_cancelled) === 1;
                    const style = anuliran ? { textDecoration: 'line-through' } : undefined;
                    return (
                      <tr key={it.id} className={anuliran ? 'muted' : undefined}>
                        <td className="muted">{it.line_no}</td>
                        <td style={style}>
                          {it.description}
                          {Number(it.is_gift) === 1 && (
                            <span className="badge muted" style={{ marginLeft: 6 }}>подарък</span>
                          )}
                        </td>
                        <td className="muted" style={style}>{it.batch_no ?? '—'}</td>
                        <td className="num" style={style}>{quantity(it.quantity)} {it.unit}</td>
                        <td className="num" style={style}>
                          {Number(it.is_gift) === 1 ? '—' : unitPrice(it, it.currency)}
                        </td>
                        <td className="num" style={style}>{money(lineSum(it), it.currency)}</td>
                      </tr>
                    );
                  })}
                  {doc.items.length === 0 && (
                    <tr><td colSpan={6} className="empty">Документът няма редове</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="card-body">
              <div className="totals">
                <div><span className="muted">Данъчна основа</span><span>{money(doc.subtotal, doc.currency)}</span></div>
                <div><span className="muted">ДДС</span><span>{money(doc.vat_total, doc.currency)}</span></div>
                <div className="grand"><span>Общо</span><span>{money(doc.total, doc.currency)}</span></div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function F({ k, v }) {
  return <div><div className="k">{k}</div><div className="v">{v}</div></div>;
}
